type CelloOperation = 'boot' | 'changeLanguage'

interface CelloOperationOptions extends Record<CelloOperation, unknown> {
  boot: {
    productId: string
    token: string
    language: string
    productUserDetails: {
      firstName: string
      lastName: string
      fullName: string
      email: string
    }
  }
  changeLanguage: string
}

type Cello = <T extends CelloOperation>(operation: T, options: CelloOperationOptions[T]) => Promise<void>

type CelloAttributionOperation = 'allowCookies' | 'getUcc'

type CelloAttributionReturnType<T extends CelloAttributionOperation> = T extends 'allowCookies' ? never : string

type CelloAttribution = <T extends CelloAttributionOperation>(operation: T) => Promise<CelloAttributionReturnType<T>>

declare global {
  interface Window {
    Cello?: Cello
    CelloAttribution?: CelloAttribution
  }
}

interface ProvidedCello {
  instance: Cello | null
  attribution: CelloAttribution | null
}

export default defineNuxtPlugin({
  name: 'cello',
  dependsOn: ['i18n'],
  setup() {
    const { $cookiebot } = useNuxtApp()
    const { cello: celloSettings, mode } = useRuntimeConfig().public

    const { logError } = useErrorHandling()
    const { userRepository } = useApi()
    const { language } = storeToRefs(useLanguageStore())
    const userStore = useUserStore()
    const directSignStore = useDirectSignStore()

    const cello = reactive<ProvidedCello>({
      instance: null,
      attribution: null,
    })

    if (!['productId', 'widgetScript', 'attributionScript'].every(key => Boolean(celloSettings[key]))) {
      if (mode === 'production' || mode === 'staging') {
        logError('Required Cello environment variables not provided', new Error())
      }

      return {
        provide: {
          cello,
        },
      }
    }

    const initialiseCello = async () => {
      try {
        const resp = await userRepository.retrieveCelloToken()

        await cello.instance?.('boot', {
          productId: celloSettings.productId,
          token: resp.token,
          language: language.value,
          productUserDetails: {
            firstName: userStore.firstName,
            lastName: userStore.lastName,
            fullName: `${userStore.firstName} ${userStore.lastName}`.trim(),
            email: userStore.email,
          },
        })
      } catch (error) {
        logError('Cello initialisation failed', error)
      }
    }

    const isRegularUserSignedIn = computed(() => {
      return Boolean(userStore.id && !directSignStore.isDirectSign)
    })

    const { status: widgetScriptStatus, onError: onCelloInstanceError } = useScript(
      {
        src: celloSettings.widgetScript,
        type: 'module',
        async: true,
      },
      {
        trigger: isRegularUserSignedIn,
      }
    )

    onCelloInstanceError(error => {
      logError('Loading Cello widget script failed', error)
    })

    // We cannot rely on a script loaded event to initialise Cello, as their
    // script will take some time to add its properties to the window.
    // Therefore, we need to 'listen' for when the property is set on the
    // window, and act on it then.
    Object.defineProperty(window, 'Cello', {
      get() {
        return cello.instance
      },
      set(value) {
        cello.instance = value
      },
    })

    const unwatchCello = watchEffect(() => {
      if (widgetScriptStatus.value === 'loaded' && cello.instance) {
        void initialiseCello()
        unwatchCello()
      }
    })

    const changeLanguage = async (newLanguage: string) => {
      try {
        await cello.instance?.('changeLanguage', newLanguage)
      } catch (error) {
        logError('Cello language change failed', error)
      }
    }

    watch(language, newLanguage => {
      void changeLanguage(newLanguage)
    })

    const route = useRoute()
    const isSigningUpBusiness = computed(() => route.name === 'business-signup')

    const { onError: onCelloAttributionError } = useScript(
      {
        src: celloSettings.attributionScript,
        type: 'module',
        async: true,
      },
      {
        trigger: isSigningUpBusiness,
      }
    )

    onCelloAttributionError(error => {
      logError('Loading Cello attribution script failed', error)
    })

    Object.defineProperty(window, 'CelloAttribution', {
      get() {
        return cello.attribution
      },
      set(value) {
        cello.attribution = value
      },
    })

    const allowCookies = async () => {
      try {
        await cello.attribution?.('allowCookies')
      } catch (error) {
        logError('Cello attribution allow cookies failed', error)
      }
    }

    const unwatchCookies = watchEffect(() => {
      if (cello.attribution && $cookiebot.instance?.consented) {
        void allowCookies()
        unwatchCookies()
      }
    })

    return {
      provide: {
        cello,
      },
    }
  },
})
